import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit2, Trash2, Package } from "lucide-react";
import { fmtBRL, fmtNum } from "../lib/format";
import { toast } from "sonner";

const empty = { nome: "", codigo: "", ean: "", sku: "", descricao: "", categoria: "", marca: "", representada_id: "", preco_tabela: 0, custo: 0, unidade: "UN", status: "ativo" };

export default function Produtos() {
  const [list, setList] = useState([]);
  const [reps, setReps] = useState([]);
  const [q, setQ] = useState("");
  const [rep, setRep] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get("/produtos", { params: rep !== "all" ? { representada_id: rep } : {} }); setList(data); };
  useEffect(() => { load(); }, [rep]);
  useEffect(() => { api.get("/representadas").then((r) => setReps(r.data)); }, []);
  const save = async () => {
    try { if (editing) await api.put(`/produtos/${editing}`, form); else await api.post("/produtos", form); toast.success("Salvo"); setOpen(false); setForm(empty); setEditing(null); load(); }
    catch { toast.error("Erro"); }
  };
  const edit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/produtos/${id}`); load(); };
  const filtered = list.filter((p) => { const s = q.toLowerCase(); return !s || (p.nome || "").toLowerCase().includes(s) || (p.codigo || "").toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s); });
  const repName = (id) => reps.find((r) => r.id === id)?.nome_fantasia || "-";

  return (
    <div>
      <PageHeader title="Produtos" subtitle="Catálogo geral filtrável por representada" actions={
        <Button data-testid="btn-new-produto" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-[#075BFF] hover:bg-[#087BFF]"><Plus className="w-4 h-4 mr-1" />Novo produto</Button>
      } />
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input placeholder="Buscar por nome, SKU, código..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" data-testid="inp-search-produto" />
        <Select value={rep} onValueChange={setRep}>
          <SelectTrigger className="w-56" data-testid="select-representada-produto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas representadas</SelectItem>
            {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome_fantasia}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((p) => (
          <div key={p.id} className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow">
            <div className="h-32 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center mb-3">
              <Package className="w-10 h-10 text-slate-400" />
            </div>
            <div className="text-xs text-slate-500">{repName(p.representada_id)} · {p.marca}</div>
            <div className="font-semibold text-sm line-clamp-2 mt-1">{p.nome}</div>
            <div className="flex justify-between text-xs text-slate-500 mt-2"><span className="font-mono">{p.sku}</span><span>{p.unidade}</span></div>
            <div className="mt-3 flex items-center justify-between">
              <div className="font-mono font-bold text-blue-600 dark:text-blue-400">{fmtBRL(p.preco_tabela)}</div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => edit(p)} data-testid={`btn-edit-produto-${p.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} produto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["nome", "Nome"], ["codigo", "Código"], ["sku", "SKU"], ["ean", "EAN"], ["categoria", "Categoria"], ["marca", "Marca"], ["ncm", "NCM"], ["unidade", "Unidade"], ["embalagem", "Embalagem"]].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label>{l}</Label><Input data-testid={`prod-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div className="space-y-1"><Label>Representada</Label>
              <Select value={form.representada_id || ""} onValueChange={(v) => setForm({ ...form, representada_id: v })}>
                <SelectTrigger data-testid="prod-representada"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome_fantasia}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Preço tabela</Label><Input type="number" step="0.01" value={form.preco_tabela} onChange={(e) => setForm({ ...form, preco_tabela: parseFloat(e.target.value || 0) })} /></div>
            <div className="space-y-1"><Label>Custo</Label><Input type="number" step="0.01" value={form.custo} onChange={(e) => setForm({ ...form, custo: parseFloat(e.target.value || 0) })} /></div>
            <div className="space-y-1"><Label>Qtd por caixa</Label><Input type="number" value={form.qtd_por_caixa || 1} onChange={(e) => setForm({ ...form, qtd_por_caixa: parseInt(e.target.value || 0) })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button data-testid="btn-save-produto" onClick={save} className="bg-[#075BFF] hover:bg-[#087BFF]">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
